import * as React from "react";
import { RotatingHints, TypingIndicator, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * The 12 curated Atlas tips from unified#367, verbatim. Product voice,
 * empathetic, no em-dashes, each names something the user can do today.
 */
const ATLAS_TIPS = [
  "Attach files straight into the chat, Atlas reads them this turn.",
  "Ask Atlas to search the web and fold the latest into its answer.",
  "Create a note, quiz, podcast, or video without leaving the chat.",
  'Say "publish it" and Atlas publishes to your studio, honestly confirmed.',
  "Everything a session touches collects in the Context panel beside you.",
  "Click any item in the Context panel for a rich preview and actions.",
  "Ask for a chart or table and Atlas renders it inline.",
  'Atlas keeps your goal across the session, say "continue" anytime.',
  "Answer Atlas's questions with one tap, it marks its recommended pick.",
  "Search your whole workspace in plain words, results group by type.",
  "Switch Fast and Pro modes per message from the composer.",
  "Paste a link and Atlas reads the page into the conversation.",
];

export function RotatingHintsDemo() {
  return (
    <Section
      id="rotating-hints"
      title="RotatingHints"
      description="Rotating usage tips for chat wait states. Mount it in the thinking or long-tool-run row while the assistant works, and unmount it on the first prose token. It stays silent through a short grace (so a fast answer never flashes a tip), then cycles the list on a fixed cadence with the house two-beat slide-then-fade, collapsing to a calm cross-fade under reduced motion. Decorative by design (aria-hidden), so it never competes with the streamed answer. Demo cadence is sped up; the real defaults are a 3s grace and a 10s rotation."
    >
      <VariantBlock
        label="In a wait row"
        caption="thinking indicator + rotating tip (the intended mount)"
      >
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <TypingIndicator variant="aiko" />
          <RotatingHints hints={ATLAS_TIPS} intervalMs={2600} graceMs={700} className="max-w-sm" />
        </div>
      </VariantBlock>

      <VariantBlock
        label="Grace period"
        caption="replay to watch the quiet beat before the first tip lands"
      >
        <GraceReplay />
      </VariantBlock>

      <VariantBlock label="Truncation" caption="an overlong tip stays a single line and ellipsizes">
        <div className="w-64 rounded-lg border border-border bg-muted/30 px-3 py-2">
          <RotatingHints
            hints={[
              "This tip is deliberately far too long to fit on one line so you can see it truncate.",
              "A short one.",
            ]}
            intervalMs={2600}
            graceMs={400}
          />
        </div>
      </VariantBlock>
    </Section>
  );
}

/**
 * Remounts the rotator on demand (via a changing `key`) so the reviewer can
 * replay the grace: a beat of silence, then the first tip fades in.
 */
function GraceReplay() {
  const [nonce, setNonce] = React.useState(0);
  return (
    <div className="flex items-center gap-3">
      <div className="flex min-w-[18rem] items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
        <TypingIndicator />
        <RotatingHints
          key={nonce}
          hints={ATLAS_TIPS}
          intervalMs={2600}
          graceMs={1500}
          className="max-w-sm"
        />
      </div>
      <Button variant="outline" size="sm" onClick={() => setNonce((n) => n + 1)}>
        Replay grace
      </Button>
    </div>
  );
}
